import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import type { TFunction } from 'i18next';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Resolver, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { z } from 'zod';
import { QueryKey } from '@/Config/api/queryKey';
import type { EmailChangeRequested, User } from '@/Domain/api/User/dto/User.dto';

const BAD_REQUEST_STATUS = 400;
const FORBIDDEN_STATUS = 403;
const CONFLICT_STATUS = 409;
const TOO_MANY_REQUESTS_STATUS = 429;

// Mirrors the server cap: the address is written to `auth_user.username` too.
const EMAIL_MAX_LENGTH = 150;

// Built per validation run, not at module import, so errors follow a language switch.
const buildUserEmailValidator = (t: TFunction) =>
  z.object({
    email: z
      .string()
      .trim()
      .email(t('validation:invalid_email'))
      .max(EMAIL_MAX_LENGTH, t('validation:character_limit', { max: EMAIL_MAX_LENGTH })),
    current_password: z.string().min(1, t('validation:password_required')),
  });

export type UserEmailForm = z.infer<ReturnType<typeof buildUserEmailValidator>>;

type ApiErrorBody = { errors?: Record<string, { message: string; code: string }[]> };

export const useUserEmail = (user: User) => {
  const { t } = useTranslation(['user_profile', 'validation']);
  // Reached through a ref, not a dependency: a changing resolver identity would
  // make react-hook-form re-validate on every render.
  const tRef = useRef(t);
  tRef.current = t;
  const resolver = useCallback<Resolver<UserEmailForm>>(
    (values, context, options) =>
      zodResolver(buildUserEmailValidator(tRef.current))(values, context, options),
    [],
  );
  // Deliberately not persisted: nothing has changed server-side, so a reload
  // honestly drops back to the resting state.
  const [pendingEmail, setPendingEmail] = useState<string | null>(null);

  const form = useForm<UserEmailForm>({
    defaultValues: { email: user?.email || '', current_password: '' },
    resolver,
  });

  const mutation = useMutation<EmailChangeRequested, AxiosError<ApiErrorBody>, UserEmailForm>({
    mutationKey: [QueryKey.user.emailChange],
    onSuccess: (data) => {
      // The user query is deliberately not written to: nothing has changed yet.
      setPendingEmail(data.pending_email);
      // setValue, not resetField: resetField no-ops for a never-registered field.
      form.setValue('current_password', '');
    },
    onError: (error) => {
      const status = error.response?.status;
      if (status === BAD_REQUEST_STATUS && error.response?.data?.errors?.current_password) {
        form.setError('current_password', { message: t('form.contact_email_wrong_password') });
        return;
      }
      if (status === CONFLICT_STATUS) {
        form.setError('email', { message: t('form.contact_email_taken') });
        return;
      }
      if (status === FORBIDDEN_STATUS) {
        form.setError('email', { message: t('form.contact_email_no_password') });
        return;
      }
      if (status === TOO_MANY_REQUESTS_STATUS) {
        form.setError('email', { message: t('form.contact_email_throttled') });
        return;
      }
      toast.error(t('toast.error'));
    },
  });

  // Deliberately not wired to useAutoSubmit: it would mail every half-typed address.
  const handleRequestEmailChange = form.handleSubmit((data) => mutation.mutate(data));

  const emailValue = form.watch('email');
  const currentEmail = user?.email || '';
  const isEmailChanged = emailValue.trim() !== currentEmail;

  // Confirming happens outside this form, so the row has to follow the refetched user.
  const { reset, formState } = form;
  const isDirty = formState.dirtyFields.email;
  useEffect(() => {
    // The link was clicked. Runs regardless of the dirty flag, which is still set
    // from before the submit even though the completed state renders no input.
    if (pendingEmail !== null) {
      if (currentEmail === pendingEmail) {
        setPendingEmail(null);
        reset({ email: currentEmail, current_password: '' });
      }
      return;
    }
    // Otherwise follow a background change, but never overwrite in-progress typing.
    if (isDirty) return;
    reset({ email: currentEmail, current_password: '' });
  }, [currentEmail, isDirty, pendingEmail, reset]);

  const resetToCurrent = () => {
    setPendingEmail(null);
    form.reset({ email: user?.email || '', current_password: '' });
  };

  return {
    form,
    handleRequestEmailChange,
    handleCancel: resetToCurrent,
    isPending: mutation.isPending,
    // The address a link was just sent to; null until a request succeeds.
    pendingEmail,
    currentEmail,
    isEmailChanged,
  };
};
